import React, { useState } from 'react';
import styled from 'styled-components';
import { theme } from 'styles/theme';

interface CollapsibleDataCardProps {
  title: string;
  totalCount: number;
  summaryInfo: {
    label: string;
    value: string | number;
    type?: 'success' | 'warning' | 'error' | 'info';
  }[];
  children: React.ReactNode;
}

const CollapsibleDataCard: React.FC<CollapsibleDataCardProps> = ({ 
  title, 
  totalCount, 
  summaryInfo, 
  children 
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  return (
    <CardContainer>
      <SummarySection onClick={toggleExpanded} clickable>
        <CardHeader>
          <h3>{title}</h3>
          <ExpandIcon expanded={isExpanded}>▼</ExpandIcon>
        </CardHeader>
        
        <MainStats>
          <TotalCount>{formatNumber(totalCount)}</TotalCount>
          <SummaryGrid>
            {summaryInfo.map((info, index) => (
              <SummaryItem key={index} type={info.type}>
                <SummaryLabel>{info.label}</SummaryLabel>
                <SummaryValue>{info.value}</SummaryValue>
              </SummaryItem>
            ))}
          </SummaryGrid>
        </MainStats>
      </SummarySection>

      {isExpanded && (
        <DetailPane>
          {children}
        </DetailPane>
      )}
    </CardContainer>
  );
};

const CardContainer = styled.div`
  background: white;
  border: 1px solid ${theme.border.light};
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
`;

const SummarySection = styled.div<{ clickable?: boolean }>`
  padding: 20px;
  cursor: ${props => props.clickable ? 'pointer' : 'default'};
  
  &:hover {
    background-color: ${props => props.clickable ? '#f8f9fa' : 'transparent'};
  }
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  
  h3 {
    margin: 0;
    color: ${theme.color.main};
    font-size: 18px;
    font-weight: 600;
  }
`;

const ExpandIcon = styled.span<{ expanded: boolean }>`
  font-size: 12px;
  color: ${theme.color.subtext};
  transition: transform 0.3s ease;
  transform: rotate(${props => props.expanded ? '180deg' : '0deg'});
`;

const MainStats = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TotalCount = styled.div`
  font-size: 32px;
  font-weight: 700;
  color: ${theme.color.main};
  text-align: center;
`;

const SummaryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
  gap: 12px;
`;

const SummaryItem = styled.div<{ type?: 'success' | 'warning' | 'error' | 'info' }>`
  text-align: center;
  padding: 8px;
  border-radius: 4px;
  background: ${props => {
    switch (props.type) {
      case 'success': return '#f0fdf4';
      case 'warning': return '#fffbeb';
      case 'error': return '#fef2f2';
      case 'info': return '#f0f9ff';
      default: return '#f8f9fa';
    }
  }};
  border: 1px solid ${props => {
    switch (props.type) {
      case 'success': return '#bbf7d0';
      case 'warning': return '#fcd34d';
      case 'error': return '#fecaca';
      case 'info': return '#bfdbfe';
      default: return '#e9ecef';
    }
  }};
`;

const SummaryLabel = styled.div`
  font-size: 12px;
  color: ${theme.color.subtext};
  font-weight: 500;
  margin-bottom: 4px;
`;

const SummaryValue = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${theme.color.text};
`;

const DetailPane = styled.div`
  border-top: 1px solid ${theme.border.light};
  background-color: #f9fafb;
  padding: 20px;
  animation: slideDown 0.3s ease;
  
  @keyframes slideDown {
    from {
      opacity: 0;
      max-height: 0;
    }
    to {
      opacity: 1;
      max-height: 500px;
    }
  }
`;

export default CollapsibleDataCard; 